/**
 * 角色表单对话框
 */
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select } from 'antd';
import { message } from '@/utils/antdStatic';
import { createRole, updateRole } from '../../api/role';
import type { Role, RoleFormData } from '../../api/types';

interface RoleFormProps {
  visible: boolean;
  role: Role | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const RoleForm: React.FC<RoleFormProps> = ({
  visible,
  role,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const isEdit = !!role;

  useEffect(() => {
    if (visible && role) {
      form.setFieldsValue({
        name: role.name,
        code: role.code,
        status: role.status,
        note: role.note,
        // REQ-20260521-0007: 编辑模式回显 portal_access，后端 List/Get 已透出
        portalAccess: role.portalAccess && role.portalAccess.length > 0
          ? role.portalAccess
          : ['admin'],
        assignmentSubject: role.applicableSubjects?.length === 1 ? role.applicableSubjects[0] : undefined,
        usageScopes: role.usageScopes ?? (role.applicableSubjects?.[0] === 'customer' ? ['descendant_tenants'] : ['owner_tenant']),
      });
    } else if (visible) {
      form.resetFields();
      form.setFieldsValue({
        status: 1,
        // REQ-20260521-0007: 默认勾选「管理后台」，与后端 fnd_roles.portal_access DEFAULT 对齐
        portalAccess: ['admin'],
        assignmentSubject: 'internal_user',
        usageScopes: ['owner_tenant'],
      });
    }
  }, [visible, role, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);
      const data: RoleFormData = {
        name: values.name,
        code: values.code,
        status: values.status,
        note: values.note,
        // REQ-20260521-0007: 始终透传 portalAccess（request.ts 拦截器自动转 snake_case）。
        // 后端 sanitizeRolePortalAccess 处理空/非法值（兜底 ["admin"] 防止访问失锁）。
        portalAccess: Array.isArray(values.portalAccess) ? values.portalAccess : ['admin'],
        applicableSubjects: [values.assignmentSubject],
        usageScopes: values.assignmentSubject === 'customer' ? ['descendant_tenants'] : values.usageScopes,
      };

      if (isEdit) {
        await updateRole(role!.id, data);
        message.success('更新成功');
      } else {
        await createRole(data);
        message.success('创建成功');
      }

      onSuccess();
    } catch (error: unknown) {
      if (typeof error === 'object' && error !== null && 'errorFields' in error) return;
      console.error('提交失败', error);
    } finally {
      setSubmitLoading(false);
    }
  };

  // REQ-20260521-0007: 角色可访问门户选项。
  // 仅暴露 admin / mall（platform 是平台保留字，租户端 UI 不开放）。
  const PORTAL_OPTIONS = [
    { label: '管理后台 (admin)', value: 'admin' as const },
    { label: '采购商城 (mall)', value: 'mall' as const },
  ];
  const SUBJECT_OPTIONS = [
    { label: '员工', value: 'internal_user' as const },
    { label: '客户开户账号', value: 'customer' as const },
  ];

  return (
    <Modal
      title={isEdit ? '编辑角色' : '新增角色'}
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={submitLoading}
      width={560}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: 1,
        }}
      >
        <Form.Item
          name="name"
          label="角色名称"
          rules={[{ required: true, message: '请输入角色名称' }]}
        >
          <Input placeholder="请输入角色名称" maxLength={50} />
        </Form.Item>

        <Form.Item
          name="code"
          label="角色编码"
          rules={[
            { required: true, message: '请输入角色编码' },
            { pattern: /^[A-Za-z0-9_-]+$/, message: '只能包含字母、数字、下划线和连字符' },
          ]}
        >
          <Input placeholder="请输入角色编码" maxLength={50} disabled={isEdit} />
        </Form.Item>

        <Form.Item
          name="status"
          label="状态"
          rules={[{ required: true, message: '请选择状态' }]}
        >
          <Select placeholder="请选择状态">
            <Select.Option value={1}>启用</Select.Option>
            <Select.Option value={0}>禁用</Select.Option>
          </Select>
        </Form.Item>

        {/* REQ-20260521-0007: 角色可访问门户。空选 → 后端兜底 ["admin"]；登录时与其他角色 union 写入 JWT available_portals。 */}
        <Form.Item
          name="portalAccess"
          label="可访问门户"
          tooltip="选择该角色允许访问的门户。勾选「采购商城」后，绑定该角色的用户登录后可进入 /mall。不选则后端兜底为「管理后台」。"
          rules={[
            {
              validator: async (_, value) => {
                if (!value || !Array.isArray(value)) return;
                for (const v of value) {
                  if (v !== 'admin' && v !== 'mall') {
                    throw new Error('仅允许选择 admin / mall');
                  }
                }
              },
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="选择角色可访问门户"
            options={PORTAL_OPTIONS}
            allowClear
            maxTagCount="responsive"
            data-testid="role-select-portal-access"
          />
        </Form.Item>

        <Form.Item name="assignmentSubject" label="使用对象" rules={[{ required: true, message: '请选择员工或客户开户账号' }]}>
          <Select options={SUBJECT_OPTIONS} onChange={(value) => form.setFieldValue('usageScopes', value === 'customer' ? ['descendant_tenants'] : ['owner_tenant'])} />
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prev, next) => prev.assignmentSubject !== next.assignmentSubject}>
          {({ getFieldValue }) => getFieldValue('assignmentSubject') === 'customer' ? (
            <Form.Item label="使用范围"><span>下级客户开户（所有层级共用）</span></Form.Item>
          ) : (
            <Form.Item name="usageScopes" label="使用范围" rules={[{ required: true, type: 'array', min: 1, message: '至少选择一个使用范围' }]} extra="允许下级选用角色不代表可以查看下级公司的数据。">
              <Select mode="multiple" options={[{ label: '本租户', value: 'owner_tenant' }, { label: '全部下级租户', value: 'descendant_tenants' }]} />
            </Form.Item>
          )}
        </Form.Item>

        <Form.Item
          name="note"
          label="备注"
        >
          <Input.TextArea
            placeholder="请输入备注"
            rows={3}
            maxLength={200}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RoleForm;
